import { Shuffle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  applyOutfitPreset,
  BACKPACK_OPTIONS,
  BOTTOM_STYLE_OPTIONS,
  EXPRESSION_OPTIONS,
  EYE_COLOR_SWATCHES,
  FACIAL_HAIR_OPTIONS,
  GLASSES_OPTIONS,
  HAIR_STYLE_OPTIONS,
  HAIR_SWATCHES,
  HAT_COLOR_SWATCHES,
  HAT_OPTIONS,
  JEWELRY_OPTIONS,
  normalizeAvatarLook,
  OUTFIT_PRESETS,
  PANTS_SWATCHES,
  randomAvatarLook,
  SHIRT_SWATCHES,
  SHOE_STYLE_OPTIONS,
  SHOE_SWATCHES,
  SKIN_SWATCHES,
  TOP_STYLE_OPTIONS,
} from "@/lib/office/avatar-look";
import { cn } from "@/lib/utils";
import { VyllageAvatarPreview } from "./vyllage-elements";
import type { AvatarColors } from "./world-engine";

type AvatarChapter = "body" | "style" | "flair";

const CHAPTERS: Array<{ id: AvatarChapter; label: string }> = [
  { id: "body", label: "Body" },
  { id: "style", label: "Style" },
  { id: "flair", label: "Flair" },
];

type AvatarStudioProps = {
  name: string;
  onNameChange: (name: string) => void;
  colors: AvatarColors;
  onColorChange: (key: keyof AvatarColors, value: string) => void;
  onColorsReplace?: (colors: AvatarColors) => void;
  onRandomize?: () => void;
  /**
   * card — small preview + controls (legacy dual-card)
   * modal — onboarding / in-world compact
   * tray — dressing room: controls only (stage owns the hero preview)
   */
  layout?: "card" | "modal" | "tray";
  /** Override inline preview size; ignored when showPreview is false */
  previewSize?: number;
  showPreview?: boolean;
  disabled?: boolean;
};

export function randomAvatarColors(): AvatarColors {
  return randomAvatarLook();
}

export function AvatarStudio({
  name,
  onNameChange,
  colors: colorsRaw,
  onColorChange,
  onColorsReplace,
  onRandomize,
  layout = "card",
  previewSize,
  showPreview,
  disabled = false,
}: AvatarStudioProps) {
  const colors = normalizeAvatarLook(colorsRaw);
  const isModal = layout === "modal";
  const isTray = layout === "tray";
  const [chapter, setChapter] = useState<AvatarChapter>("body");
  const renderPreview = showPreview ?? !isTray;
  const size = previewSize ?? (isModal ? 112 : isTray ? 0 : 128);

  const replaceColors = (next: AvatarColors) => {
    if (onColorsReplace) {
      onColorsReplace(next);
      return;
    }
    for (const [k, v] of Object.entries(next)) {
      onColorChange(k as keyof AvatarColors, v);
    }
  };

  const randomize = () => {
    if (onRandomize && !onColorsReplace) {
      onRandomize();
      return;
    }
    replaceColors(randomAvatarLook());
  };

  const applyPreset = (presetId: string) => {
    replaceColors(applyOutfitPreset(colors, presetId));
  };

  return (
    <div
      className={
        isTray
          ? "flex w-full flex-col gap-3"
          : isModal
            ? "flex w-full flex-col gap-3 sm:flex-row sm:items-start"
            : "flex flex-col gap-4 sm:flex-row sm:items-start"
      }
    >
      {renderPreview ? (
        <div className="mx-auto flex shrink-0 flex-col items-center gap-2 sm:mx-0 sm:sticky sm:top-0">
          <div className="rounded-xl bg-muted/60 p-2">
            <VyllageAvatarPreview colors={colors} size={size} spin />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={disabled}
            onClick={randomize}
          >
            <Shuffle className="h-3.5 w-3.5" />
            Randomize
          </Button>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col gap-3 text-left">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Label htmlFor="avatar-display-name">Name on the map</Label>
            <Input
              id="avatar-display-name"
              value={name}
              disabled={disabled}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Your name in HQ"
              maxLength={64}
            />
          </div>
          {isTray ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={disabled}
              onClick={randomize}
            >
              <Shuffle className="h-3.5 w-3.5" />
              Randomize
            </Button>
          ) : null}
        </div>

        <div>
          <div className="mb-1.5 text-[10px] font-bold tracking-[0.1em] text-muted-foreground uppercase">
            Outfit presets
          </div>
          <div className="flex flex-wrap gap-1.5">
            {OUTFIT_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={disabled}
                onClick={() => applyPreset(p.id)}
                className="cursor-pointer rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-bold text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground disabled:opacity-50"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="flex flex-wrap gap-1 rounded-lg border border-border/60 bg-muted/40 p-1"
          role="tablist"
          aria-label="Avatar chapters"
        >
          {CHAPTERS.map((t) => {
            const active = chapter === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={disabled}
                onClick={() => setChapter(t.id)}
                className={cn(
                  "cursor-pointer rounded-md px-3 py-1.5 text-xs font-bold tracking-wide transition-colors disabled:opacity-50",
                  active
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div
          className="min-h-[100px] space-y-3 rounded-lg border border-border/50 bg-background/80 px-3 py-3"
          role="tabpanel"
        >
          {chapter === "body" ? (
            <>
              <SwatchRow
                label="Skin tone"
                options={SKIN_SWATCHES}
                value={colors.skin}
                disabled={disabled}
                onPick={(c) => onColorChange("skin", c)}
              />
              <SwatchRow
                label="Eye color"
                options={EYE_COLOR_SWATCHES}
                value={colors.eyeColor}
                disabled={disabled}
                onPick={(c) => onColorChange("eyeColor", c)}
              />
              <ChipRow
                label="Expression"
                options={EXPRESSION_OPTIONS}
                value={colors.expression}
                disabled={disabled}
                onPick={(id) => onColorChange("expression", id)}
              />
              <ChipRow
                label="Hair style"
                options={HAIR_STYLE_OPTIONS}
                value={colors.style}
                disabled={disabled}
                onPick={(id) => onColorChange("style", id)}
              />
              <SwatchRow
                label="Hair color"
                options={HAIR_SWATCHES}
                value={colors.hair}
                disabled={disabled}
                onPick={(c) => onColorChange("hair", c)}
              />
              <ChipRow
                label="Top style"
                options={TOP_STYLE_OPTIONS}
                value={colors.topStyle}
                disabled={disabled}
                onPick={(id) => onColorChange("topStyle", id)}
              />
              <SwatchRow
                label="Shirt / top color"
                options={SHIRT_SWATCHES}
                value={colors.shirt}
                disabled={disabled}
                onPick={(c) => onColorChange("shirt", c)}
              />
            </>
          ) : null}

          {chapter === "style" ? (
            <>
              <ChipRow
                label="Bottoms"
                options={BOTTOM_STYLE_OPTIONS}
                value={colors.bottomStyle}
                disabled={disabled}
                onPick={(id) => onColorChange("bottomStyle", id)}
              />
              <SwatchRow
                label="Pants / bottom color"
                options={PANTS_SWATCHES}
                value={colors.pants}
                disabled={disabled}
                onPick={(c) => onColorChange("pants", c)}
              />
              <ChipRow
                label="Shoe style"
                options={SHOE_STYLE_OPTIONS}
                value={colors.shoeStyle}
                disabled={disabled}
                onPick={(id) => onColorChange("shoeStyle", id)}
              />
              <SwatchRow
                label="Shoe color"
                options={SHOE_SWATCHES}
                value={colors.shoes}
                disabled={disabled}
                onPick={(c) => onColorChange("shoes", c)}
              />
              <ChipRow
                label="Hat / headwear"
                options={HAT_OPTIONS}
                value={colors.hat}
                disabled={disabled}
                onPick={(id) => onColorChange("hat", id)}
              />
              {colors.hat !== "none" ? (
                <SwatchRow
                  label="Hat color"
                  options={HAT_COLOR_SWATCHES}
                  value={colors.hatColor}
                  disabled={disabled}
                  onPick={(c) => onColorChange("hatColor", c)}
                />
              ) : (
                <p className="text-[11px] text-muted-foreground">Pick a hat to recolor it.</p>
              )}
            </>
          ) : null}

          {chapter === "flair" ? (
            <>
              <ChipRow
                label="Glasses"
                options={GLASSES_OPTIONS}
                value={colors.glasses}
                disabled={disabled}
                onPick={(id) => onColorChange("glasses", id)}
              />
              <ChipRow
                label="Facial hair"
                options={FACIAL_HAIR_OPTIONS}
                value={colors.facialHair}
                disabled={disabled}
                onPick={(id) => onColorChange("facialHair", id)}
              />
              <ChipRow
                label="Jewelry & extras"
                options={JEWELRY_OPTIONS}
                value={colors.jewelry}
                disabled={disabled}
                onPick={(id) => onColorChange("jewelry", id)}
              />
              <ChipRow
                label="Bag"
                options={BACKPACK_OPTIONS}
                value={colors.backpack}
                disabled={disabled}
                onPick={(id) => onColorChange("backpack", id)}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SwatchRow({
  label,
  options,
  value,
  onPick,
  disabled,
}: {
  label: string;
  options: readonly string[];
  value: string;
  onPick: (c: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-bold tracking-[0.1em] text-muted-foreground uppercase">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((c) => (
          <button
            key={c}
            type="button"
            disabled={disabled}
            aria-label={`${label} ${c}`}
            onClick={() => onPick(c)}
            className={cn(
              "h-6 w-6 cursor-pointer rounded-full border-2 p-0 disabled:opacity-50",
              value === c ? "border-primary ring-2 ring-primary/30" : "border-border",
            )}
            style={{ background: c }}
          />
        ))}
      </div>
    </div>
  );
}

function ChipRow({
  label,
  options,
  value,
  onPick,
  disabled,
}: {
  label: string;
  options: ReadonlyArray<{ id: string; label: string }>;
  value: string;
  onPick: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-bold tracking-[0.1em] text-muted-foreground uppercase">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((st) => {
          const active = value === st.id;
          return (
            <button
              key={st.id}
              type="button"
              disabled={disabled}
              onClick={() => onPick(st.id)}
              className={cn(
                "cursor-pointer rounded-full border px-2.5 py-1 text-[11px] font-bold disabled:opacity-50",
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:text-foreground",
              )}
            >
              {st.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
